import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, Building2, Users, MessageSquare, GripVertical, Search, Filter, Newspaper, Play, Youtube, Image, X as XIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import ImageUploader from '@/components/ImageUploader';
import VideoUploader from '@/components/VideoUploader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CMSManagement = () => {
  const [activeTab, setActiveTab] = useState('projects');
  
  // Projects state
  const [projects, setProjects] = useState([]);
  const [projectSearch, setProjectSearch] = useState('');
  const [projectFilter, setProjectFilter] = useState('all');
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [projectForm, setProjectForm] = useState({
    title: '', description: '', location: '', type: 'Residential', status: 'Ongoing',
    price_range: '', area: '', bedrooms: '', amenities: '', image_url: '', gallery_images: ''
  });
  
  // Team state
  const [team, setTeam] = useState([]);
  const [isTeamModalOpen, setIsTeamModalOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [teamForm, setTeamForm] = useState({
    name: '', position: '', bio: '', image_url: '', linkedin: '', order: 1
  });
  
  // Testimonials state
  const [testimonials, setTestimonials] = useState([]);
  const [isTestimonialModalOpen, setIsTestimonialModalOpen] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [testimonialForm, setTestimonialForm] = useState({
    client_name: '', company: '', rating: 5, message: '', image_url: '', video_url: '', testimonial_type: 'Customer', profession: '', guntha_size: ''
  });

  // News & Media state
  const [newsMedia, setNewsMedia] = useState([]);
  const [isNewsModalOpen, setIsNewsModalOpen] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const [newsForm, setNewsForm] = useState({
    title: '', description: '', video_type: 'youtube', video_url: '', thumbnail_url: '', category: 'News', is_featured: false
  });

  // Social Activities state
  const [socialActivities, setSocialActivities] = useState([]);
  const [isSocialModalOpen, setIsSocialModalOpen] = useState(false);
  const [editingSocial, setEditingSocial] = useState(null);
  const [socialForm, setSocialForm] = useState({ type: 'image', url: '', caption: '', category: 'Team Activities' });
  const SOCIAL_CATEGORIES = ['Team Activities', 'Events', 'Achievements', 'Site Visits', 'Celebrations', 'CSR', 'Other'];

  // Helper to convert YouTube URL to embed format
  const convertYouTubeUrl = (url) => {
    if (!url) return '';
    // Handle various YouTube URL formats
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s?]+)/,
      /youtube\.com\/shorts\/([^&\s?]+)/
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return `https://www.youtube.com/embed/${match[1]}`;
      }
    }
    return url; // Return as-is if not a YouTube URL
  };

  const getAuthHeader = () => {
    const token = localStorage.getItem('crm_token');
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    fetchProjects();
    fetchTeam();
    fetchTestimonials();
    fetchNewsMedia();
    fetchSocialActivities();
  }, []);

  // Fetch functions
  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
    }
  };

  const fetchTeam = async () => {
    try {
      const response = await axios.get(`${API}/team`);
      setTeam(response.data);
    } catch (error) {
    }
  };

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/testimonials`);
      setTestimonials(response.data);
    } catch (error) {
    }
  };

  const fetchNewsMedia = async () => {
    try {
      const response = await axios.get(`${API}/news-media`);
      setNewsMedia(response.data);
    } catch (error) {
    }
  };

  const fetchSocialActivities = async () => {
    try {
      const res = await axios.get(`${API}/social-activities`);
      setSocialActivities(res.data || []);
    } catch {}
  };

  const handleSocialSubmit = async (e) => {
    e.preventDefault();
    if (!socialForm.url) { toast.error('Please upload an image or video'); return; }
    try {
      if (editingSocial) {
        await axios.put(`${API}/social-activities/${editingSocial.id}`, socialForm, { headers: getAuthHeader() });
        toast.success('Updated');
      } else {
        await axios.post(`${API}/social-activities`, socialForm, { headers: getAuthHeader() });
        toast.success('Added');
      }
      setIsSocialModalOpen(false);
      setEditingSocial(null);
      setSocialForm({ type: 'image', url: '', caption: '', category: 'Team Activities' });
      fetchSocialActivities();
    } catch { toast.error('Failed to save'); }
  };

  const handleSocialDelete = async (id) => {
    if (!window.confirm('Delete this activity?')) return;
    try {
      await axios.delete(`${API}/social-activities/${id}`, { headers: getAuthHeader() });
      toast.success('Deleted');
      fetchSocialActivities();
    } catch { toast.error('Delete failed'); }
  };

  const openSocialModal = (item = null) => {
    if (item) { setEditingSocial(item); setSocialForm({ type: item.type, url: item.url, caption: item.caption || '', category: item.category || 'Team Activities' }); }
    else { setEditingSocial(null); setSocialForm({ type: 'image', url: '', caption: '', category: 'Team Activities' }); }
    setIsSocialModalOpen(true);
  };

  // Project handlers
  const filteredProjects = projects.filter(p => {
    const matchesSearch = p.title.toLowerCase().includes(projectSearch.toLowerCase()) ||
                         p.location.toLowerCase().includes(projectSearch.toLowerCase());
    const matchesFilter = projectFilter === 'all' || p.type.toLowerCase() === projectFilter.toLowerCase();
    return matchesSearch && matchesFilter;
  });

  const openProjectModal = (project = null) => {
    if (project) {
      setEditingProject(project);
      setProjectForm({
        ...project,
        amenities: project.amenities?.join(', ') || '',
        gallery_images: project.gallery_images?.join(', ') || ''
      });
    } else {
      setEditingProject(null);
      setProjectForm({
        title: '', description: '', location: '', type: 'Residential', status: 'Ongoing',
        price_range: '', area: '', bedrooms: '', amenities: '', image_url: '', gallery_images: ''
      });
    }
    setIsProjectModalOpen(true);
  };

  const handleProjectSubmit = async (e) => {
    e.preventDefault();
    const submitData = {
      ...projectForm,
      amenities: projectForm.amenities.split(',').map(a => a.trim()).filter(a => a),
      gallery_images: projectForm.gallery_images.split(',').map(url => url.trim()).filter(url => url)
    };

    try {
      if (editingProject) {
        await axios.put(`${API}/projects/${editingProject.id}`, submitData, { headers: getAuthHeader() });
        toast.success('Project updated successfully!');
      } else {
        await axios.post(`${API}/projects`, submitData, { headers: getAuthHeader() });
        toast.success('Project created successfully!');
      }
      fetchProjects();
      setIsProjectModalOpen(false);
    } catch (error) {
      toast.error('Failed to save project');
    }
  };

  const handleProjectDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    try {
      await axios.delete(`${API}/projects/${id}`, { headers: getAuthHeader() });
      toast.success('Project deleted!');
      fetchProjects();
    } catch (error) {
      toast.error('Failed to delete project');
    }
  };

  // Team handlers
  const openTeamModal = (member = null) => {
    if (member) {
      setEditingMember(member);
      setTeamForm({ ...member });
    } else {
      setEditingMember(null);
      setTeamForm({ name: '', position: '', bio: '', image_url: '', linkedin: '', order: team.length + 1 });
    }
    setIsTeamModalOpen(true);
  };

  const handleTeamSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingMember) {
        await axios.put(`${API}/team/${editingMember.id}`, teamForm, { headers: getAuthHeader() });
        toast.success('Team member updated!');
      } else {
        await axios.post(`${API}/team`, teamForm, { headers: getAuthHeader() });
        toast.success('Team member added!');
      }
      fetchTeam();
      setIsTeamModalOpen(false);
    } catch (error) {
      toast.error('Failed to save team member');
    }
  };

  const handleTeamDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this team member?')) return;
    try {
      await axios.delete(`${API}/team/${id}`, { headers: getAuthHeader() });
      toast.success('Team member deleted!');
      fetchTeam();
    } catch (error) {
      toast.error('Failed to delete team member');
    }
  };

  // Testimonial handlers
  const openTestimonialModal = (testimonial = null) => {
    if (testimonial) {
      setEditingTestimonial(testimonial);
      setTestimonialForm({ ...testimonial });
    } else {
      setEditingTestimonial(null);
      setTestimonialForm({ client_name: '', company: '', rating: 5, message: '', image_url: '', video_url: '', testimonial_type: 'Customer', profession: '', guntha_size: '' });
    }
    setIsTestimonialModalOpen(true);
  };

  const handleTestimonialSubmit = async (e) => {
    e.preventDefault();
    // Convert YouTube URL to embed format before saving
    const submitData = {
      ...testimonialForm,
      video_url: convertYouTubeUrl(testimonialForm.video_url)
    };
    try {
      if (editingTestimonial) {
        await axios.put(`${API}/testimonials/${editingTestimonial.id}`, submitData, { headers: getAuthHeader() });
        toast.success('Testimonial updated!');
      } else {
        await axios.post(`${API}/testimonials`, submitData, { headers: getAuthHeader() });
        toast.success('Testimonial added!');
      }
      fetchTestimonials();
      setIsTestimonialModalOpen(false);
    } catch (error) {
      toast.error('Failed to save testimonial');
    }
  };

  const handleTestimonialDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this testimonial?')) return;
    try {
      await axios.delete(`${API}/testimonials/${id}`, { headers: getAuthHeader() });
      toast.success('Testimonial deleted!');
      fetchTestimonials();
    } catch (error) {
      toast.error('Failed to delete testimonial');
    }
  };

  // News & Media handlers
  const openNewsModal = (news = null) => {
    if (news) {
      setEditingNews(news);
      setNewsForm({ ...news });
    } else {
      setEditingNews(null);
      setNewsForm({ title: '', description: '', video_type: 'youtube', video_url: '', thumbnail_url: '', category: 'News', is_featured: false });
    }
    setIsNewsModalOpen(true);
  };

  const handleNewsSubmit = async (e) => {
    e.preventDefault();
    const submitData = {
      ...newsForm,
      video_url: newsForm.video_type === 'youtube' ? convertYouTubeUrl(newsForm.video_url) : newsForm.video_url
    };
    try {
      if (editingNews) {
        await axios.put(`${API}/news-media/${editingNews.id}`, submitData, { headers: getAuthHeader() });
        toast.success('News item updated!');
      } else {
        await axios.post(`${API}/news-media`, submitData, { headers: getAuthHeader() });
        toast.success('News item added!');
      }
      fetchNewsMedia();
      setIsNewsModalOpen(false);
    } catch (error) {
      toast.error('Failed to save news item');
    }
  };

  const handleNewsDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this news item?')) return;
    try {
      await axios.delete(`${API}/news-media/${id}`, { headers: getAuthHeader() });
      toast.success('News item deleted!');
      fetchNewsMedia();
    } catch (error) {
      toast.error('Failed to delete news item');
    }
  };

  return (
    <div className="p-6 lg:p-8" data-testid="cms-management-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-stone-900 mb-2">Content Management</h1>
        <p className="text-stone-600">Manage your website content - Projects, Team, Testimonials & News</p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-5 mb-6">
          <TabsTrigger value="projects" className="flex items-center gap-2" data-testid="cms-projects-tab">
            <Building2 className="w-4 h-4" /> Projects ({projects.length})
          </TabsTrigger>
          <TabsTrigger value="team" className="flex items-center gap-2" data-testid="cms-team-tab">
            <Users className="w-4 h-4" /> Team ({team.length})
          </TabsTrigger>
          <TabsTrigger value="testimonials" className="flex items-center gap-2" data-testid="cms-testimonials-tab">
            <MessageSquare className="w-4 h-4" /> Testimonials ({testimonials.length})
          </TabsTrigger>
          <TabsTrigger value="news" className="flex items-center gap-2" data-testid="cms-news-tab">
            <Newspaper className="w-4 h-4" /> News & Media ({newsMedia.length})
          </TabsTrigger>
          <TabsTrigger value="social" className="flex items-center gap-2" data-testid="cms-social-tab">
            <Image className="w-4 h-4" /> Social ({socialActivities.length})
          </TabsTrigger>
        </TabsList>

        {/* Projects Tab */}
        <TabsContent value="projects">
          <div className="bg-white rounded-lg border border-stone-200 p-6">
            <div className="flex flex-col sm:flex-row gap-4 mb-6 justify-between">
              <div className="flex gap-3 flex-1">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                  <Input
                    placeholder="Search projects..."
                    value={projectSearch}
                    onChange={(e) => setProjectSearch(e.target.value)}
                    className="pl-10"
                    data-testid="project-search"
                  />
                </div>
                <Select value={projectFilter} onValueChange={setProjectFilter}>
                  <SelectTrigger className="w-40" data-testid="project-filter">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Filter" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="plotting">Plotting</SelectItem>
                    <SelectItem value="residential">Residential</SelectItem>
                    <SelectItem value="commercial">Commercial</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={() => openProjectModal()} className="bg-emerald-900 hover:bg-emerald-800" data-testid="add-project-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Project
              </Button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-stone-50">
                  <tr>
                    <th className="text-left p-3 text-xs uppercase tracking-wider text-stone-600">Project</th>
                    <th className="text-left p-3 text-xs uppercase tracking-wider text-stone-600">Type</th>
                    <th className="text-left p-3 text-xs uppercase tracking-wider text-stone-600">Status</th>
                    <th className="text-left p-3 text-xs uppercase tracking-wider text-stone-600">Location</th>
                    <th className="text-right p-3 text-xs uppercase tracking-wider text-stone-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {filteredProjects.map((project) => (
                    <tr key={project.id} className="hover:bg-stone-50" data-testid={`project-row-${project.id}`}>
                      <td className="p-3">
                        <div className="flex items-center gap-3">
                          {project.image_url ? (
                            <img src={project.image_url} alt={project.title} className="w-12 h-12 object-cover rounded" />
                          ) : (
                            <div className="w-12 h-12 bg-stone-100 rounded flex items-center justify-center text-stone-400"><Image className="w-5 h-5" /></div>
                          )}
                          <span className="font-medium text-stone-900">{project.title}</span>
                        </div>
                      </td>
                      <td className="p-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          project.type === 'Residential' ? 'bg-blue-100 text-blue-700' :
                          project.type === 'Commercial' ? 'bg-purple-100 text-purple-700' :
                          'bg-green-100 text-green-700'
                        }`}>
                          {project.type}
                        </span>
                      </td>
                      <td className="p-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          project.status === 'Completed' ? 'bg-emerald-100 text-emerald-700' :
                          project.status === 'Ongoing' ? 'bg-amber-100 text-amber-700' :
                          'bg-stone-100 text-stone-700'
                        }`}>
                          {project.status}
                        </span>
                      </td>
                      <td className="p-3 text-stone-600 text-sm max-w-[200px] truncate">{project.location}</td>
                      <td className="p-3 text-right">
                        <Button variant="ghost" size="sm" onClick={() => openProjectModal(project)} data-testid={`edit-project-${project.id}`}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={() => handleProjectDelete(project.id)} data-testid={`delete-project-${project.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredProjects.length === 0 && (
                <div className="text-center py-12 text-stone-500">No projects found</div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Team Tab */}
        <TabsContent value="team">
          <div className="bg-white rounded-lg border border-stone-200 p-6">
            <div className="flex justify-between items-center mb-6">
              <p className="text-stone-600">Manage leadership team members displayed on the About Us page</p>
              <Button onClick={() => openTeamModal()} className="bg-emerald-900 hover:bg-emerald-800" data-testid="add-team-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Member
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {team.sort((a, b) => a.order - b.order).map((member) => (
                <div key={member.id} className="bg-stone-50 rounded-lg p-4 relative group" data-testid={`team-card-${member.id}`}>
                  <div className="flex items-start gap-4">
                    {member.image_url ? (
                      <img src={member.image_url} alt={member.name} className="w-16 h-16 rounded-full object-cover" />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-stone-100 flex items-center justify-center text-stone-400"><Users className="w-6 h-6" /></div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-stone-900 truncate">{member.name}</h3>
                      <p className="text-amber-600 text-sm">{member.position}</p>
                      <p className="text-stone-600 text-sm mt-1 line-clamp-2">{member.bio}</p>
                    </div>
                  </div>
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openTeamModal(member)} data-testid={`edit-team-${member.id}`}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleTeamDelete(member.id)} data-testid={`delete-team-${member.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="absolute bottom-2 left-2 text-xs text-stone-400">Order: {member.order}</div>
                </div>
              ))}
            </div>
            {team.length === 0 && (
              <div className="text-center py-12 text-stone-500">No team members yet</div>
            )}
          </div>
        </TabsContent>

        {/* Testimonials Tab */}
        <TabsContent value="testimonials">
          <div className="bg-white rounded-lg border border-stone-200 p-6">
            <div className="flex justify-between items-center mb-6">
              <p className="text-stone-600">Manage customer testimonials displayed on the website</p>
              <Button onClick={() => openTestimonialModal()} className="bg-emerald-900 hover:bg-emerald-800" data-testid="add-testimonial-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Testimonial
              </Button>
            </div>

            <div className="space-y-4">
              {testimonials.map((testimonial) => (
                <div key={testimonial.id} className="bg-stone-50 rounded-lg p-4 relative group" data-testid={`testimonial-card-${testimonial.id}`}>
                  <div className="flex items-start gap-4">
                    {testimonial.video_url ? (
                      <div className="w-24 h-16 bg-stone-900 rounded overflow-hidden flex-shrink-0">
                        <iframe
                          src={testimonial.video_url}
                          title="Video"
                          className="w-full h-full"
                          style={{ pointerEvents: 'none' }}
                        />
                      </div>
                    ) : testimonial.image_url ? (
                      <img src={testimonial.image_url} alt={testimonial.client_name} className="w-12 h-12 rounded-full object-cover" />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-stone-100 flex items-center justify-center text-stone-400"><Users className="w-5 h-5" /></div>
                    )}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className="font-bold text-stone-900">{testimonial.client_name}</h3>
                        {testimonial.company && (
                          <>
                            <span className="text-stone-400">|</span>
                            <span className="text-stone-600 text-sm">{testimonial.company}</span>
                          </>
                        )}
                        <div className="flex ml-2">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <span key={i} className="text-amber-500">★</span>
                          ))}
                        </div>
                        {testimonial.video_url && (
                          <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded font-medium">VIDEO</span>
                        )}
                        <span className={`px-2 py-0.5 text-xs rounded font-medium ${
                          testimonial.testimonial_type === 'Channel Partner' ? 'bg-purple-100 text-purple-700' :
                          testimonial.testimonial_type === 'Guntha' ? 'bg-green-100 text-green-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {testimonial.testimonial_type || 'Customer'}
                        </span>
                      </div>
                      <p className="text-stone-600 text-sm italic line-clamp-2">"{testimonial.message}"</p>
                      {testimonial.guntha_size && (
                        <span className="text-amber-600 text-xs font-bold mt-1 inline-block">{testimonial.guntha_size}</span>
                      )}
                    </div>
                  </div>
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openTestimonialModal(testimonial)} data-testid={`edit-testimonial-${testimonial.id}`}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleTestimonialDelete(testimonial.id)} data-testid={`delete-testimonial-${testimonial.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            {testimonials.length === 0 && (
              <div className="text-center py-12 text-stone-500">No testimonials yet</div>
            )}
          </div>
        </TabsContent>

        {/* News & Media Tab */}
        <TabsContent value="news">
          <div className="bg-white rounded-lg border border-stone-200 p-6">
            <div className="flex justify-between items-center mb-6">
              <p className="text-stone-600">Manage news articles and media coverage videos</p>
              <Button onClick={() => openNewsModal()} className="bg-emerald-900 hover:bg-emerald-800" data-testid="add-news-btn">
                <Plus className="w-4 h-4 mr-2" /> Add News/Media
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {newsMedia.map((item) => (
                <div key={item.id} className="bg-stone-50 rounded-lg overflow-hidden relative group" data-testid={`news-card-${item.id}`}>
                  <div className="aspect-video bg-stone-900 relative">
                    {item.video_type === 'youtube' ? (
                      <iframe
                        src={item.video_url}
                        title={item.title}
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    ) : (
                      <video
                        src={item.video_url}
                        className="w-full h-full object-cover"
                        controls
                      />
                    )}
                    <div className="absolute top-2 left-2 flex gap-1">
                      {item.video_type === 'youtube' ? (
                        <span className="px-2 py-0.5 bg-red-600 text-white text-xs rounded font-medium flex items-center gap-1">
                          <Youtube className="w-3 h-3" /> YouTube
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 bg-blue-600 text-white text-xs rounded font-medium flex items-center gap-1">
                          <Play className="w-3 h-3" /> Video
                        </span>
                      )}
                      {item.is_featured && (
                        <span className="px-2 py-0.5 bg-amber-500 text-white text-xs rounded font-medium">Featured</span>
                      )}
                    </div>
                  </div>
                  <div className="p-4">
                    <span className={`px-2 py-0.5 text-xs rounded font-medium mb-2 inline-block ${
                      item.category === 'News' ? 'bg-blue-100 text-blue-700' :
                      item.category === 'Media Coverage' ? 'bg-purple-100 text-purple-700' :
                      'bg-green-100 text-green-700'
                    }`}>
                      {item.category}
                    </span>
                    <h3 className="font-bold text-stone-900 line-clamp-2">{item.title}</h3>
                    {item.description && (
                      <p className="text-stone-600 text-sm mt-1 line-clamp-2">{item.description}</p>
                    )}
                  </div>
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                    <Button variant="ghost" size="sm" className="bg-white/80 hover:bg-white" onClick={() => openNewsModal(item)} data-testid={`edit-news-${item.id}`}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" className="bg-white/80 hover:bg-white text-red-600" onClick={() => handleNewsDelete(item.id)} data-testid={`delete-news-${item.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            {newsMedia.length === 0 && (
              <div className="text-center py-12 text-stone-500">No news or media items yet</div>
            )}
          </div>
        </TabsContent>

        {/* Social Activities Tab */}
        <TabsContent value="social">
          <div className="bg-white rounded-lg border border-stone-200 p-6">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="font-semibold text-stone-800 text-lg">Social Activities</h3>
                <p className="text-stone-500 text-sm mt-0.5">Photos & videos shown on the public Social Activities page</p>
              </div>
              <Button onClick={() => openSocialModal()} className="bg-emerald-700 hover:bg-emerald-800" data-testid="add-social-btn">
                <Plus className="w-4 h-4 mr-1" /> Add Photo/Video
              </Button>
            </div>
            {socialActivities.length === 0 ? (
              <div className="text-center py-12 text-stone-400">No social activities yet. Add your first one!</div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {socialActivities.map(item => (
                  <div key={item.id} className="group relative rounded-xl overflow-hidden border border-stone-200 aspect-square" data-testid={`social-item-${item.id}`}>
                    {item.type === 'video' ? (
                      <video src={item.url} className="w-full h-full object-cover" />
                    ) : (
                      <img src={item.url} alt={item.caption || ''} className="w-full h-full object-cover" />
                    )}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                      <button onClick={() => openSocialModal(item)} className="p-2 bg-white rounded-full text-stone-700 hover:text-blue-600" data-testid={`edit-social-${item.id}`}>
                        <Edit className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleSocialDelete(item.id)} className="p-2 bg-white rounded-full text-stone-700 hover:text-red-600" data-testid={`delete-social-${item.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    {item.caption && (
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1.5">
                        <p className="text-white text-[10px] truncate">{item.caption}</p>
                      </div>
                    )}
                    {item.type === 'video' && (
                      <div className="absolute top-2 right-2 bg-black/50 rounded px-1.5 py-0.5 text-white text-[9px] font-bold">VIDEO</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Social Activities Modal */}
      <Dialog open={isSocialModalOpen} onOpenChange={setIsSocialModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingSocial ? 'Edit Activity' : 'Add Photo / Video'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSocialSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-stone-700 mb-1 block">Type</label>
              <Select value={socialForm.type} onValueChange={v => setSocialForm(f => ({...f, type: v, url: ''}))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="image">Image / Photo</SelectItem>
                  <SelectItem value="video">Video</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {socialForm.type === 'image' ? (
              <ImageUploader
                initialImageUrl={socialForm.url}
                onImageUploaded={url => setSocialForm(f => ({...f, url}))}
              />
            ) : (
              <VideoUploader
                initialType="uploaded"
                initialUrl={socialForm.url}
                onVideoUploaded={(_, url) => setSocialForm(f => ({...f, url}))}
              />
            )}
            <Input placeholder="Caption (optional)" value={socialForm.caption}
              onChange={e => setSocialForm(f => ({...f, caption: e.target.value}))}
              data-testid="social-caption-input" />
            <div>
              <label className="text-sm font-medium text-stone-700 mb-1 block">Category</label>
              <Select value={socialForm.category} onValueChange={v => setSocialForm(f => ({...f, category: v}))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SOCIAL_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="submit" className="flex-1 bg-emerald-700 hover:bg-emerald-800" data-testid="save-social-btn">
                {editingSocial ? 'Update' : 'Add'}
              </Button>
              <Button type="button" variant="outline" onClick={() => setIsSocialModalOpen(false)}>Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Project Modal */}
      <Dialog open={isProjectModalOpen} onOpenChange={setIsProjectModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="project-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              {editingProject ? 'Edit Project' : 'Add New Project'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleProjectSubmit} className="space-y-4">
            <Input
              placeholder="Project Title *"
              value={projectForm.title}
              onChange={(e) => setProjectForm({ ...projectForm, title: e.target.value })}
              required
              data-testid="project-title-input"
            />
            <Textarea
              placeholder="Description *"
              value={projectForm.description}
              onChange={(e) => setProjectForm({ ...projectForm, description: e.target.value })}
              required
              rows={4}
              data-testid="project-desc-input"
            />
            <Input
              placeholder="Location *"
              value={projectForm.location}
              onChange={(e) => setProjectForm({ ...projectForm, location: e.target.value })}
              required
            />
            <div className="grid grid-cols-2 gap-4">
              <Select value={projectForm.type} onValueChange={(v) => setProjectForm({ ...projectForm, type: v })}>
                <SelectTrigger><SelectValue placeholder="Type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Plotting">Plotting</SelectItem>
                  <SelectItem value="Residential">Residential</SelectItem>
                  <SelectItem value="Commercial">Commercial</SelectItem>
                </SelectContent>
              </Select>
              <Select value={projectForm.status} onValueChange={(v) => setProjectForm({ ...projectForm, status: v })}>
                <SelectTrigger><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Ongoing">Ongoing</SelectItem>
                  <SelectItem value="Completed">Completed</SelectItem>
                  <SelectItem value="Upcoming">Upcoming</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <Input placeholder="Price Range" value={projectForm.price_range} onChange={(e) => setProjectForm({ ...projectForm, price_range: e.target.value })} />
              <Input placeholder="Area (sq ft)" value={projectForm.area} onChange={(e) => setProjectForm({ ...projectForm, area: e.target.value })} />
              <Input placeholder="Bedrooms" value={projectForm.bedrooms} onChange={(e) => setProjectForm({ ...projectForm, bedrooms: e.target.value })} />
            </div>
            <Input
              placeholder="Amenities (comma separated)"
              value={projectForm.amenities}
              onChange={(e) => setProjectForm({ ...projectForm, amenities: e.target.value })}
            />
            <div>
              <label className="text-sm font-medium text-stone-700 mb-2 block">Main Image</label>
              <ImageUploader
                initialImageUrl={projectForm.image_url}
                onImageUploaded={(url) => setProjectForm(prev => ({ ...prev, image_url: url }))}
                label="Click to upload main project image"
              />
              <Input
                placeholder="Or paste image URL"
                value={projectForm.image_url || ''}
                onChange={(e) => setProjectForm(prev => ({ ...prev, image_url: e.target.value }))}
                className="mt-2"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-stone-700 mb-2 block">Gallery (Images &amp; Videos)</label>
              {/* Thumbnail grid of existing gallery items */}
              {(() => {
                const raw = projectForm.gallery_images || '';
                const urls = (Array.isArray(raw) ? raw : raw.split(',').map(u => u.trim())).filter(u => u);
                const isVid = (url) => /\.(mp4|mov|avi|mkv|webm|wmv|flv|m4v|3gp|ogv)$/i.test(url);
                return urls.length > 0 ? (
                  <div className="grid grid-cols-4 gap-2 mb-3">
                    {urls.map((url, i) => (
                      <div key={i} className="relative group">
                        {isVid(url) ? (
                          <div className="w-full h-16 rounded border border-stone-200 bg-stone-900 flex items-center justify-center">
                            <svg className="w-5 h-5 text-stone-400" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                          </div>
                        ) : (
                          <img src={url} alt={`Gallery ${i + 1}`} className="w-full h-16 object-cover rounded border border-stone-200" />
                        )}
                        <button type="button" onClick={() =>
                          setProjectForm(prev => {
                            const cur = (Array.isArray(prev.gallery_images)
                              ? prev.gallery_images
                              : prev.gallery_images.split(',').map(u => u.trim())
                            ).filter(u => u);
                            return { ...prev, gallery_images: cur.filter((_, idx) => idx !== i).join(', ') };
                          })
                        } className="absolute top-0.5 right-0.5 bg-red-500 text-white p-0.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                          <XIcon className="w-2.5 h-2.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : null;
              })()}
              <ImageUploader
                multiple={true}
                label="Add images or videos"
                onImageUploaded={(urls) => setProjectForm(prev => {
                  const cur = (Array.isArray(prev.gallery_images)
                    ? prev.gallery_images
                    : prev.gallery_images.split(',').map(u => u.trim())
                  ).filter(u => u);
                  const newUrls = Array.isArray(urls) ? urls : [urls];
                  return { ...prev, gallery_images: [...cur, ...newUrls].join(', ') };
                })}
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="submit" className="flex-1 bg-emerald-900 hover:bg-emerald-800" data-testid="save-project-btn">
                {editingProject ? 'Update Project' : 'Create Project'}
              </Button>
              <Button type="button" variant="outline" onClick={() => setIsProjectModalOpen(false)}>Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Team Modal */}
      <Dialog open={isTeamModalOpen} onOpenChange={setIsTeamModalOpen}>
        <DialogContent className="max-w-lg" data-testid="team-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              {editingMember ? 'Edit Team Member' : 'Add Team Member'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleTeamSubmit} className="space-y-4">
            <Input
              placeholder="Full Name *"
              value={teamForm.name}
              onChange={(e) => setTeamForm({ ...teamForm, name: e.target.value })}
              required
              data-testid="team-name-input"
            />
            <Input
              placeholder="Position / Title *"
              value={teamForm.position}
              onChange={(e) => setTeamForm({ ...teamForm, position: e.target.value })}
              required
            />
            <Textarea
              placeholder="Bio / Description"
              value={teamForm.bio}
              onChange={(e) => setTeamForm({ ...teamForm, bio: e.target.value })}
              rows={3}
            />
            <Input
              placeholder="LinkedIn URL (optional)"
              value={teamForm.linkedin || ''}
              onChange={(e) => setTeamForm({ ...teamForm, linkedin: e.target.value })}
            />
            <Input
              type="number"
              placeholder="Display Order"
              value={teamForm.order}
              onChange={(e) => setTeamForm({ ...teamForm, order: parseInt(e.target.value) || 1 })}
              min={1}
            />
            <div>
              <label className="text-sm font-medium text-stone-700 mb-2 block">Profile Photo</label>
              <ImageUploader
                initialImageUrl={teamForm.image_url}
                onImageUploaded={(url) => setTeamForm(prev => ({ ...prev, image_url: url }))}
                label="Click to upload profile photo"
              />
              <Input
                placeholder="Or paste image URL"
                value={teamForm.image_url || ''}
                onChange={(e) => setTeamForm(prev => ({ ...prev, image_url: e.target.value }))}
                className="mt-2"
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="submit" className="flex-1 bg-emerald-900 hover:bg-emerald-800" data-testid="save-team-btn">
                {editingMember ? 'Update Member' : 'Add Member'}
              </Button>
              <Button type="button" variant="outline" onClick={() => setIsTeamModalOpen(false)}>Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Testimonial Modal */}
      <Dialog open={isTestimonialModalOpen} onOpenChange={setIsTestimonialModalOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="testimonial-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              {editingTestimonial ? 'Edit Testimonial' : 'Add Testimonial'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleTestimonialSubmit} className="space-y-4">
            <Input
              placeholder="Client Name *"
              value={testimonialForm.client_name}
              onChange={(e) => setTestimonialForm({ ...testimonialForm, client_name: e.target.value })}
              required
              data-testid="testimonial-name-input"
            />
            <Input
              placeholder="Company / Organization"
              value={testimonialForm.company}
              onChange={(e) => setTestimonialForm({ ...testimonialForm, company: e.target.value })}
            />
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-stone-700 mb-2 block">Type *</label>
                <Select value={testimonialForm.testimonial_type} onValueChange={(v) => setTestimonialForm({ ...testimonialForm, testimonial_type: v })}>
                  <SelectTrigger><SelectValue placeholder="Type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Customer">Customer</SelectItem>
                    <SelectItem value="Channel Partner">Channel Partner</SelectItem>
                    <SelectItem value="Guntha">Land Size (Guntha)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700 mb-2 block">Rating</label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setTestimonialForm({ ...testimonialForm, rating: star })}
                      className={`text-2xl ${star <= testimonialForm.rating ? 'text-amber-500' : 'text-stone-300'}`}
                    >
                      ★
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Input
                placeholder="Profession (optional)"
                value={testimonialForm.profession || ''}
                onChange={(e) => setTestimonialForm({ ...testimonialForm, profession: e.target.value })}
              />
              <div>
                <Select 
                  value={testimonialForm.guntha_size || ''} 
                  onValueChange={(v) => setTestimonialForm({ ...testimonialForm, guntha_size: v })}
                >
                  <SelectTrigger className={testimonialForm.testimonial_type === 'Guntha' ? 'border-amber-500' : ''}>
                    <SelectValue placeholder="Land Size (for Guntha type)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="2 R">2 R</SelectItem>
                    <SelectItem value="5 R">5 R</SelectItem>
                    <SelectItem value="10 R">10 R</SelectItem>
                    <SelectItem value="20 R">20 R</SelectItem>
                    <SelectItem value="1 Acre">1 Acre</SelectItem>
                    <SelectItem value="2 Acre">2 Acre</SelectItem>
                    <SelectItem value="5 Acre">5 Acre</SelectItem>
                    <SelectItem value="10 Acre">10 Acre</SelectItem>
                  </SelectContent>
                </Select>
                {testimonialForm.testimonial_type === 'Guntha' && !testimonialForm.guntha_size && (
                  <p className="text-xs text-amber-600 mt-1">Required for Land Size type</p>
                )}
              </div>
            </div>
            <Textarea
              placeholder="Testimonial Message *"
              value={testimonialForm.message}
              onChange={(e) => setTestimonialForm({ ...testimonialForm, message: e.target.value })}
              required
              rows={3}
            />
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <label className="text-sm font-medium text-blue-800 mb-2 block">YouTube Video URL (optional)</label>
              <Input
                placeholder="https://www.youtube.com/watch?v=... or https://youtu.be/..."
                value={testimonialForm.video_url || ''}
                onChange={(e) => setTestimonialForm({ ...testimonialForm, video_url: e.target.value })}
                className="bg-white"
              />
              <p className="text-xs text-blue-600 mt-1">Paste any YouTube URL - it will be auto-converted to embed format</p>
              {testimonialForm.video_url && (
                <div className="mt-2 aspect-video bg-stone-900 rounded overflow-hidden">
                  <iframe
                    src={convertYouTubeUrl(testimonialForm.video_url)}
                    title="Video Preview"
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              )}
            </div>
            <div>
              <label className="text-sm font-medium text-stone-700 mb-2 block">Client Photo (optional)</label>
              <ImageUploader
                initialImageUrl={testimonialForm.image_url}
                onImageUploaded={(url) => setTestimonialForm(prev => ({ ...prev, image_url: url }))}
                label="Upload client photo"
              />
              <Input
                placeholder="Or paste image URL"
                value={testimonialForm.image_url || ''}
                onChange={(e) => setTestimonialForm(prev => ({ ...prev, image_url: e.target.value }))}
                className="mt-2"
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="submit" className="flex-1 bg-emerald-900 hover:bg-emerald-800" data-testid="save-testimonial-btn">
                {editingTestimonial ? 'Update Testimonial' : 'Add Testimonial'}
              </Button>
              <Button type="button" variant="outline" onClick={() => setIsTestimonialModalOpen(false)}>Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* News & Media Modal */}
      <Dialog open={isNewsModalOpen} onOpenChange={setIsNewsModalOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="news-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              {editingNews ? 'Edit News/Media' : 'Add News/Media'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleNewsSubmit} className="space-y-4">
            <Input
              placeholder="Title *"
              value={newsForm.title}
              onChange={(e) => setNewsForm({ ...newsForm, title: e.target.value })}
              required
              data-testid="news-title-input"
            />
            <Textarea
              placeholder="Description (optional)"
              value={newsForm.description}
              onChange={(e) => setNewsForm({ ...newsForm, description: e.target.value })}
              rows={2}
            />
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-stone-700 mb-2 block">Video Type *</label>
                <Select value={newsForm.video_type} onValueChange={(v) => setNewsForm({ ...newsForm, video_type: v, video_url: '' })}>
                  <SelectTrigger><SelectValue placeholder="Type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="youtube">YouTube Video</SelectItem>
                    <SelectItem value="uploaded">Uploaded Video</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700 mb-2 block">Category *</label>
                <Select value={newsForm.category} onValueChange={(v) => setNewsForm({ ...newsForm, category: v })}>
                  <SelectTrigger><SelectValue placeholder="Category" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="News">News</SelectItem>
                    <SelectItem value="Media Coverage">Media Coverage</SelectItem>
                    <SelectItem value="Events">Events</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <VideoUploader
              initialType={newsForm.video_type}
              initialUrl={newsForm.video_url}
              onVideoUploaded={(type, url) => setNewsForm({ ...newsForm, video_type: type, video_url: url })}
            />

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_featured"
                checked={newsForm.is_featured}
                onChange={(e) => setNewsForm({ ...newsForm, is_featured: e.target.checked })}
                className="rounded"
              />
              <label htmlFor="is_featured" className="text-sm text-stone-700">Mark as Featured</label>
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="submit" className="flex-1 bg-emerald-900 hover:bg-emerald-800" data-testid="save-news-btn">
                {editingNews ? 'Update' : 'Add News/Media'}
              </Button>
              <Button type="button" variant="outline" onClick={() => setIsNewsModalOpen(false)}>Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CMSManagement;
